/**
 * The public sheet of another player (plan 23 row 34, lane BM):
 * presentational, so the window and the offline gallery render the same
 * markup. Identity line (class-coloured name, calling icon, level, job,
 * guild), worn equipment with the shared item tooltip, and stat totals, as
 * Tome chapters on the window's parchment (Tome UI Round 5, E4). `self`
 * marks your own sheet: it says so and its tooltips compare against nothing;
 * another player's tooltips compare each piece with what you wear.
 */
import React from 'react';
import { EQUIPMENT_SCHEMA_SLOTS, schemaSlotLabel } from '../../../../../shared/equipmentSlots';
import { ITEM_DATABASE } from '../../../../../shared/ItemData';
import type { InspectSheet, InspectStats } from '../../../../../shared/social/socialWire';
import { classColor, classIcon, formatCallingName } from '../../../theme';
import { Tooltip } from '../../../components/Tooltip';
import { createItemTooltip } from '../../../components/itemTooltip';
import { ItemIcon } from '../../../components/ItemIcon';
import { rarityColor } from '../../../components/itemkit/shared';
import { useItemTooltipViewer } from '../../../hooks/useItemTooltipViewer';
import { TomeChapter, TomeChip } from '../../../primitives/tome';

const STAT_ROWS: ReadonlyArray<[keyof InspectStats, string]> = [
  ['maxHealth', 'Health'], ['maxMana', 'Mana'],
  ['strength', 'Strength'], ['dexterity', 'Dexterity'], ['intelligence', 'Intelligence'], ['luck', 'Luck'],
  ['attack', 'Attack'], ['magicAttack', 'Magic attack'], ['defense', 'Defense'], ['magicDefense', 'Magic defense'],
  ['criticalRate', 'Critical rate'], ['speed', 'Speed'],
];

/** An advanced job's id as words (`*_basic` is no advancement and is not shown). */
function formatJob(job: string): string {
  return job.split(/[_-]/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export const InspectSheetView: React.FC<{ sheet: InspectSheet; self?: boolean }> = ({ sheet, self = false }) => {
  const worn = useItemTooltipViewer();
  // Your own pieces compared with themselves would read as "no change" on every line.
  const viewer = self ? { ...worn, equipped: undefined } : worn;
  const color = classColor(sheet.calling);
  const wornCount = EQUIPMENT_SCHEMA_SLOTS.filter(slot => sheet.equipment[slot]).length;
  return (
    <div className="inspect-sheet" data-testid="inspect-sheet" data-self={self || undefined}>
      <header className="inspect-head">
        <span className="inspect-glyph" aria-hidden="true">{classIcon(sheet.calling)}</span>
        <div className="inspect-id">
          <span className="inspect-kicker">{self ? 'Your sheet' : 'Inspecting'}</span>
          <h3 className="inspect-name" style={{ '--calling': color } as React.CSSProperties}>{sheet.name}</h3>
          <div className="inspect-sub">
            <span>Level {sheet.level}</span>
            <span className="inspect-calling" style={{ '--calling': color } as React.CSSProperties}>{formatCallingName(sheet.calling)}</span>
            {sheet.job && !sheet.job.endsWith('_basic') && <span>{formatJob(sheet.job)}</span>}
          </div>
          {sheet.guildName && <div className="inspect-guild">&lt;{sheet.guildName}&gt;</div>}
        </div>
        {self && <TomeChip tone="active">You</TomeChip>}
      </header>

      <TomeChapter title={self ? 'Your equipment' : 'Their equipment'} count={wornCount}>
        {!self && <p className="inspect-note">Tooltips compare each piece with what you wear.</p>}
        <ul className="inspect-gear" aria-label="Worn equipment">
          {EQUIPMENT_SCHEMA_SLOTS.map(slot => {
            const itemId = sheet.equipment[slot];
            const item = itemId ? ITEM_DATABASE[itemId] : undefined;
            const label = schemaSlotLabel(slot);
            if (!itemId) {
              return <li key={slot} className="inspect-gear-row" data-slot={slot} data-empty="true">
                <span className="inspect-gear-slot">{label}</span><span className="inspect-gear-none">—</span>
              </li>;
            }
            const name = item?.name ?? itemId;
            return (
              <li key={slot} className="inspect-gear-row" data-slot={slot}>
                <span className="inspect-gear-slot">{label}</span>
                {/* No equip/drag hint: even your own sheet is a reading, not your bags. */}
                <Tooltip data={createItemTooltip({ id: itemId, name }, viewer)} position="left">
                  <span className="inspect-gear-item" tabIndex={0} aria-label={`${label}: ${name}`}
                    style={{ '--rarity': rarityColor(item?.rarity) ?? 'var(--tome-ink)' } as React.CSSProperties}>
                    <ItemIcon itemId={itemId} size={22} />
                    <span>{name}</span>
                  </span>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </TomeChapter>

      <TomeChapter title="Stats">
        <dl className="inspect-stats">
          {STAT_ROWS.map(([key, label]) => (
            <div key={key} className="inspect-stat">
              <dt>{label}</dt>
              <dd>{key === 'criticalRate' ? `${Number((sheet.stats[key] ?? 0).toFixed(2))}%` : Math.round(sheet.stats[key] ?? 0)}</dd>
            </div>
          ))}
        </dl>
      </TomeChapter>
    </div>
  );
};
